import ErrorIcon from '@curology/ui-components-web/react/Icon/icons/ErrorIcon';
import { Typography } from '@curology/ui-components-web/react/Typography';
import { useAtom } from 'jotai';
import React from 'react';

import { FlashMessagesState } from '@/atoms/flashMessages/flashMessagesState';
import RegisterCreditCardPaymentMethodForm from '@/components/payments/components/registerCreditCardPaymentMethodForm';
import { type NewPaymentMethod } from '@/types/payments/types';
import { type Patient } from '@/types/productSelectionAndCheckout/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

import { CHECKOUT_STATES } from '../consts/checkoutStates';

export const CcForm = ({
  checkoutState,
  patient,
  setCardLastFour,
  setCheckoutState,
  setPatient,
  setPaymentNonce,
  userMessage,
  validateOnly = false,
}: {
  checkoutState: string;
  patient: Patient;
  setCardLastFour: React.Dispatch<React.SetStateAction<string | undefined>>;
  setCheckoutState: React.Dispatch<React.SetStateAction<string>>;
  setPaymentNonce?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setPatient: React.Dispatch<React.SetStateAction<Patient>>;
  userMessage?: string | null;
  validateOnly?: boolean;
}) => {
  const [flashMessages, setFlashMessages] = useAtom(
    FlashMessagesState.flashMessagesAtom,
  );

  const onSuccess = async (newPaymentMethod: NewPaymentMethod) => {
    setCardLastFour(newPaymentMethod.cardLastFour);
    setPatient((previous) => ({ ...previous, hasCompletedCcEntry: true }));
    setPaymentNonce?.(newPaymentMethod.paymentNonce);
    setCheckoutState(
      patient.hasCompletedShippingAddressEntry
        ? CHECKOUT_STATES.ccAndShippingAddressCompleted
        : CHECKOUT_STATES.shippingAddressEntry,
    );
  };

  // Hiding with CSS instead of conditionally rendering so that Stripe works no matter how many times you go back and forth.
  return (
    <div
      className={`cur-bg-surface-neutral cur-mt-4 cur-p-4${
        checkoutState === CHECKOUT_STATES.ccEntry ? '' : ' cur-hidden'
      }`}
    >
      <Typography size="subhead" className="cur-mb-2">
        Payment method
      </Typography>
      {userMessage && (
        <Typography className="cur-mb-2" size="body">
          {userMessage}
        </Typography>
      )}
      <RegisterCreditCardPaymentMethodForm
        onSuccess={onSuccess}
        onError={(message: string) =>
          setFlashMessages([
            ...flashMessages,
            {
              children: message,
              icon: <ErrorIcon />,
              iconSize: 'sm',
              state: 'critical',
              title: 'Whoops!',
            },
          ])
        }
        onRegister={() =>
          trackEvent(AnalyticsEvent.CHECKOUT_CONTINUE_BUTTON_CLICKED, {
            card: 'payment',
          })
        }
        validateOnly={validateOnly}
      />
    </div>
  );
};
